<script lang="ts" setup>
import type { PropType} from 'vue';
import { computed } from 'vue';
import { SysarbColors } from '@/types';
import type { ICellRendererParams } from 'ag-grid-community';
import { useI18n } from 'vue-i18n';
import type { EmploymentDiscussion} from '@sysarb/sysarb-types/services/v1/discussion';
import { EmploymentDiscussionStatus } from '@sysarb/sysarb-types/services/v1/discussion';

const { t } = useI18n();

const props = defineProps({
    params: {
        type: Object as PropType<ICellRendererParams<EmploymentDiscussion, void>>,
        required: true
    }
});

const { BLUE_500, GREEN_500, GRAY_200 } = SysarbColors;

const currentStatus = computed(() => props.params.data?.DiscussionStatus ?? 0);

const statusText = computed(() => {
    // Show correct text depending on status
    switch (props.params.data?.DiscussionStatus) {
        case EmploymentDiscussionStatus.SendInvitation:
            return t('components.existingDiscussionEmployments.sendInvitation');
        case EmploymentDiscussionStatus.AnswerQuestions:
            return t('components.existingDiscussionEmployments.answerQuestions');
        case EmploymentDiscussionStatus.OngoingDiscussion:
            return t('components.existingDiscussionEmployments.ongoingDiscussion');
        case EmploymentDiscussionStatus.ConfirmationPending:
            return t('components.existingDiscussionEmployments.confirmationPending');
        case EmploymentDiscussionStatus.Archived:
            return t('components.existingDiscussionEmployments.archived');
        default:
            return '';
    }
});
</script>

<template>
    <div class="grid-x">
        <div class="sy-status-container cell auto flex-container align-middle margin-right-5">
            <div class="sy-status-bar-step" :style="{ 'border-color': currentStatus === 0 ? BLUE_500 : GREEN_500 }"></div>
            <div class="sy-status-bar-step" :style="{ 'border-color': currentStatus === 1 ? BLUE_500 : currentStatus > 1 ? GREEN_500 : GRAY_200 }"></div>
            <div class="sy-status-bar-step" :style="{ 'border-color': currentStatus === 2 ? BLUE_500 : currentStatus > 2 ? GREEN_500 : GRAY_200 }"></div>
            <div class="sy-status-bar-step" :style="{ 'border-color': currentStatus === 3 ? BLUE_500 : currentStatus > 3 ? GREEN_500 : GRAY_200 }"></div>
            <div class="sy-status-bar-step" :style="{ 'border-color': currentStatus === 4 ? GREEN_500 : GRAY_200 }"></div>
        </div>
        <div class="cell large-4">{{ statusText }}</div>
    </div>
</template>

<style lang="scss" scoped>
.sy-status-container {
    :not(:first-child) {
        margin-left: 3px;
    }
    :first-child {
        border-radius: 30px 0 0 30px;
    }
    :last-child {
        border-radius: 0 30px 30px 0;
    }
}

.sy-status-bar-step {
    flex: 1 1;
    border-top: 6px solid;
}
</style>
